import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import type {
  Control,
  FieldErrors,
  Path,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormReturn,
  UseFormSetError
} from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import * as yup from 'yup';

import { useFormStore } from '@store/use-form-store';

import { useVismaConfiguration } from '../../services/visitor-management';
import type { RegisterFormData, Registrant } from '../../types';

export function hydrateFormDraft(
  draft: RegisterFormData | null | undefined
): RegisterFormData | null {
  if (!draft) return null;
  return {
    ...draft,
    dateOfBirth: draft.dateOfBirth ? dayjs(draft.dateOfBirth) : null,
    docExpiryDate: draft.docExpiryDate ? dayjs(draft.docExpiryDate) : null
  };
}

export const INITIAL_FORM = {
  fullname: '',
  mothersName: '',
  dateOfBirth: null,
  documentType: '',
  docNo: '',
  docExpiryDate: null,
  companyName: '',
  phoneNumber: '',
  expiry: ''
};

export function usePersonalDataSchema() {
  const intl = useIntl();
  const { configuration } = useVismaConfiguration();
  const data_field = configuration?.personalData?.data_field;
  const dataProtectionActive = configuration?.dataProtection?.isActive;

  return useMemo(() => {
    const isRequired = (field: string) =>
      data_field?.[field as keyof typeof data_field]?.required;
    const isDisabled = (field: string) =>
      data_field?.[field as keyof typeof data_field]?.disabled;
    const t = (id: string) => intl.formatMessage({ id });

    const stringField = (field: string) =>
      isDisabled(field) || !isRequired(field)
        ? yup.string()
        : yup.string().required(t('PERSONAL_DATA_NEXT_ERROR'));

    const dateOfBirthField = yup
      .mixed<import('dayjs').Dayjs>()
      .nullable()
      .test(
        'isValid',
        t('INVALID_BIRTH_DATE_FORMAT'),
        (val) => !val || val.isValid()
      )
      .test('beforeCutoff', t('INVALID_BIRTH_DATE'), (val) => {
        if (!val || !val.isValid()) return true;
        return val.toDate().getTime() < new Date(2015, 0, 0).getTime();
      });

    const docExpiryDateField = yup
      .mixed<import('dayjs').Dayjs>()
      .nullable()
      .test('isValid', t('INVALID_DATE_FORMAT'), (val) => !val || val.isValid())
      .test('isFuture', t('DOC_DATE_EXPIRED'), (val) => {
        if (!val || !val.isValid()) return true;
        return val.toDate().getTime() > new Date().getTime();
      });

    return yup.object({
      fullname: stringField('fullname'),
      mothersName: stringField('mother_name'),
      dateOfBirth:
        !isDisabled('birth_date') && isRequired('birth_date')
          ? dateOfBirthField.required(t('PERSONAL_DATA_NEXT_ERROR'))
          : dateOfBirthField,
      documentType: stringField('document_type'),
      docNo: stringField('document_number'),
      docExpiryDate:
        !isDisabled('document_expiry_date') &&
        isRequired('document_expiry_date')
          ? docExpiryDateField.required(t('PERSONAL_DATA_NEXT_ERROR'))
          : docExpiryDateField,
      companyName: stringField('company_name'),
      phoneNumber: stringField('phonenumber'),
      expiry: dataProtectionActive
        ? yup.string().required(t('DATA_PROTECTION_NEXT_ERROR'))
        : yup.string()
    });
  }, [data_field, dataProtectionActive, intl]);
}

// Shared hook — call this in route screens to get form state + helpers to pass as props
export function useRegisterForm() {
  const { registerForm, setRegisterForm, clearRegisterForm } = useFormStore();
  const schema = usePersonalDataSchema();
  const [loading, setLoading] = useState(false);

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues:
      hydrateFormDraft(registerForm as unknown as RegisterFormData) ??
      INITIAL_FORM,
    mode: 'onChange'
  });

  const { setValue, reset, watch } = methods;

  useEffect(() => {
    const subscription = watch((values) =>
      setRegisterForm(values as Parameters<typeof setRegisterForm>[0])
    );
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  const setFormField = (field: Path<RegisterFormData>, value: unknown) =>
    setValue(field, value as never, { shouldValidate: true });
  const setRegistrantInfo = (registrant: RegisterFormData) =>
    reset(hydrateFormDraft(registrant) as RegisterFormData);
  const clearForm = () => {
    reset(INITIAL_FORM);
    clearRegisterForm();
  };

  return {
    methods,
    loading,
    setLoading,
    setFormField,
    setRegistrantInfo,
    clearForm
  };
}

// Context used internally by RegisterFeature's sub-components
interface RegisterPageContextValue {
  control: Control<RegisterFormData>;
  handleSubmit: UseFormHandleSubmit<RegisterFormData>;
  errors: FieldErrors<RegisterFormData>;
  isValid: boolean;
  getValues: UseFormGetValues<RegisterFormData>;
  setError: UseFormSetError<RegisterFormData>;
  loading: boolean;
  setLoading: (v: boolean) => void;
  registeredPersons: Registrant[];
  editRegistrantId: string | null;
  setEditRegistrantId: (id: string | null) => void;
  setFormField: (field: Path<RegisterFormData>, value: unknown) => void;
  setRegistrantInfo: (registrant: RegisterFormData) => void;
  clearForm: () => void;
  addRegistrant: (registrant: Registrant) => void;
  removeRegistrant: (docNo: string) => void;
  updateRegistrant: (registrant: Registrant) => void;
}

export const RegisterPageContext =
  createContext<RegisterPageContextValue | null>(null);

interface RegisterPageContextProviderProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  methods: UseFormReturn<any, any, any>;
  loading: boolean;
  setLoading: (v: boolean) => void;
  setFormField: (field: Path<RegisterFormData>, value: unknown) => void;
  setRegistrantInfo: (registrant: RegisterFormData) => void;
  clearForm: () => void;
  registeredPersons: Registrant[];
  editRegistrantId: string | null;
  setEditRegistrantId: (id: string | null) => void;
  addRegistrant: (registrant: Registrant) => void;
  removeRegistrant: (docNo: string) => void;
  updateRegistrant: (registrant: Registrant) => void;
  children: React.ReactNode;
}

// RegisterFeature sets this context from props — sub-components read from it
export function RegisterPageContextProvider({
  methods,
  loading,
  setLoading,
  setFormField,
  setRegistrantInfo,
  clearForm,
  registeredPersons,
  editRegistrantId,
  setEditRegistrantId,
  addRegistrant,
  removeRegistrant,
  updateRegistrant,
  children
}: RegisterPageContextProviderProps) {
  return (
    <FormProvider {...methods}>
      <RegisterPageContext.Provider
        value={{
          control: methods.control,
          handleSubmit: methods.handleSubmit,
          errors: methods.formState.errors,
          isValid: methods.formState.isValid,
          getValues: methods.getValues,
          setError: methods.setError,
          loading,
          setLoading,
          registeredPersons,
          editRegistrantId,
          setEditRegistrantId,
          setFormField,
          setRegistrantInfo,
          clearForm,
          addRegistrant,
          removeRegistrant,
          updateRegistrant
        }}
      >
        {children}
      </RegisterPageContext.Provider>
    </FormProvider>
  );
}

export const useRegisterPage = () => useContext(RegisterPageContext);
